import "@/App.css";
import { Component, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { I18nProvider } from "@/context/I18nContext";
import AutoTranslate from "@/components/AutoTranslate";
import MoneyTransition from "@/components/MoneyTransition";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { trackPage } from "@/lib/track";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { BuddyToast } from "@/components/Buddy";
import PushNotifications from "@/components/PushNotifications";
const Chatbot = lazy(() => import("@/components/Chatbot"));
const TradeChatBubble = lazy(() => import("@/components/TradeChatBubble"));

// ── Eagerly loaded (critical path) ──────────────────────────────────────────
import Landing from "@/pages/Landing";

// ── Lazily loaded (split into separate chunks) ───────────────────────────────
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const TwoFactorChallenge = lazy(() => import("@/pages/TwoFactorChallenge"));
const StaffSetup = lazy(() => import("@/pages/StaffSetup"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("@/pages/TermsConditions"));
const AmlKycPolicy = lazy(() => import("@/pages/AmlKycPolicy"));
const AcceptableUse = lazy(() => import("@/pages/AcceptableUse"));
const Contact = lazy(() => import("@/pages/Contact"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const SellCards = lazy(() => import("@/pages/SellCards"));
const SellCard = lazy(() => import("@/pages/SellCard"));
const GiftCardBalance = lazy(() => import("@/pages/GiftCardBalance"));
import BuyGiftCard from "@/pages/BuyGiftCard";
import CartPage from "@/pages/CartPage";
import MyGiftCardOrders from "@/pages/user/MyGiftCardOrders";
const MarketplacePage = lazy(() => import("@/pages/MarketplacePage"));

// ── User pages ───────────────────────────────────────────────────────────────
const UserLayout = lazy(() => import("@/pages/user/UserLayout"));
const UserDashboard = lazy(() => import("@/pages/user/UserDashboard"));
const MyExchanges = lazy(() => import("@/pages/user/MyExchanges"));
const Wallet = lazy(() => import("@/pages/user/Wallet"));
const SendCard = lazy(() => import("@/pages/user/SendCard"));
const Transactions = lazy(() => import("@/pages/user/Transactions"));
const Support = lazy(() => import("@/pages/user/Support"));
const Profile = lazy(() => import("@/pages/user/Profile"));
const Settings = lazy(() => import("@/pages/user/Settings"));
const VerifyEmail = lazy(() => import("@/pages/user/VerifyEmail"));
const Kyc = lazy(() => import("@/pages/user/Kyc"));
const Marketplace = lazy(() => import("@/pages/user/Marketplace"));
const Rewards = lazy(() => import("@/pages/user/Rewards"));
const RateAlerts = lazy(() => import("@/pages/user/RateAlerts"));

// ── Admin pages ──────────────────────────────────────────────────────────────
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminExchanges = lazy(() => import("@/pages/admin/AdminExchanges"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminGiftCards = lazy(() => import("@/pages/admin/AdminGiftCards"));
const AdminTransactions = lazy(() => import("@/pages/admin/AdminTransactions"));
const AdminTickets = lazy(() => import("@/pages/admin/AdminTickets"));
const AdminKyc = lazy(() => import("@/pages/admin/AdminKyc"));
const AdminWithdrawals = lazy(() => import("@/pages/admin/AdminWithdrawals"));
const AdminNotifications = lazy(() => import("@/pages/admin/AdminNotifications"));
const AdminLogs = lazy(() => import("@/pages/admin/AdminLogs"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminPayoutMethods = lazy(() => import("@/pages/admin/AdminPayoutMethods"));
const AdminBroadcasts = lazy(() => import("@/pages/admin/AdminBroadcasts"));
const AdminAccounting = lazy(() => import("@/pages/admin/AdminAccounting"));
const AdminLiveFeed = lazy(() => import("@/pages/admin/AdminLiveFeed"));
const AdminWorkers = lazy(() => import("@/pages/admin/AdminWorkers"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminMarketplace = lazy(() => import("@/pages/admin/AdminMarketplace"));
const AdminAds = lazy(() => import("@/pages/admin/AdminAds"));
const AdminAdProfit = lazy(() => import("@/pages/admin/AdminAdProfit"));
const AdminSocial = lazy(() => import("@/pages/admin/AdminSocial"));
const AdminEmailCampaigns = lazy(() => import("@/pages/admin/AdminEmailCampaigns"));
const AdminAIStudio = lazy(() => import("@/pages/admin/AdminAIStudio"));
const AdminRewards = lazy(() => import("@/pages/admin/AdminRewards"));
const AdminSafety = lazy(() => import("@/pages/admin/AdminSafety"));
const AdminGoogleAds = lazy(() => import("@/pages/admin/AdminGoogleAds"));
const AdminTikTok = lazy(() => import("@/pages/admin/AdminTikTok"));
const AdminVideoStudio = lazy(() => import("@/pages/admin/AdminVideoStudio"));
const AdminSystem = lazy(() => import("@/pages/admin/AdminSystem"));
import AdminGiftStore from "@/pages/admin/AdminGiftStore";

// ── SEO Helmet helper ────────────────────────────────────────────────────────
function SEO({ title, description, canonical, noindex = false }) {
    const base = "https://gifttocash.com";
    return (
        <Helmet>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={`${base}${canonical}`} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={`${base}${canonical}`} />
        </Helmet>
    );
}

// Simple loading fallback
function PageLoader() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-cream">
            <div className="w-8 h-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
        </div>
    );
}

// Floating support chat on every page (not in the admin area, which has its own tools)
function GlobalChat() {
    const { pathname } = useLocation();
    if (pathname.startsWith("/admin")) return null;
    return <Suspense fallback={null}><Chatbot /><Safe><TradeChatBubble /></Safe></Suspense>;
}

// If a helper widget ever crashes, hide just that widget instead of blanking the whole site
class Safe extends Component {
    constructor(props) { super(props); this.state = { bad: false }; }
    static getDerivedStateFromError() { return { bad: true }; }
    componentDidCatch(err) { try { console.error("widget hidden:", err); } catch { /* ignore */ } }
    render() { return this.state.bad ? null : this.props.children; }
}

function PageTracker() {
    const location = useLocation();
    useEffect(() => {
        api.post("/track/pageview").catch(() => {});
        trackPage(location.pathname);
        // open every new page at the top (links to #sections still jump to their section)
        if (!location.hash) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
}

// ── SEO Wrapper per route ────────────────────────────────────────────────────
function LandingWithSEO() {
    return (<><SEO title="Sell Gift Cards for Cash — Instant Payouts & Best Rates" description="Sell your gift cards for cash in Canada & USA. Instant payouts, secure transactions, and a simple gift card exchange for Amazon, Apple, and more. Sell now." canonical="/" /><Landing /></>);
}
function LoginWithSEO() {
    return (<><SEO title="Login — GiftToCash" description="Log in to your GiftToCash account to sell gift cards and manage your payouts." canonical="/login" noindex={true} /><Login /></>);
}
function RegisterWithSEO() {
    return (<><SEO title="Create Account — GiftToCash" description="Sign up for free and start selling your unused gift cards for instant cash on GiftToCash." canonical="/register" /><Register /></>);
}
function MarketplacePageWithSEO() {
    return (<><SEO title="Gift Card Marketplace — Buy & Sell Gift Cards | GiftToCash" description="Buy discounted gift cards below face value or sell yours for up to 95% cash. Escrow-protected P2P marketplace. Amazon, iTunes, Xbox and more." canonical="/marketplace" /><MarketplacePage /></>);
}
function BlogWithSEO() {
    return (<><SEO title="Blog — Gift Card Tips & Money Guides | GiftToCash" description="Learn how to get the most cash for your gift cards, money saving tips, and gift card exchange guides." canonical="/blog" /><Blog /></>);
}
function ReviewsWithSEO() {
    return (<><SEO title="Reviews & Trust — GiftToCash" description="Read real reviews from people who sold gift cards on GiftToCash, and see how we keep every sale safe and clear." canonical="/reviews" /><Reviews /></>);
}
function ContactWithSEO() {
    return (<><SEO title="Contact Us — GiftToCash" description="Get in touch with the GiftToCash support team. We're here to help with exchanges, payouts and account questions." canonical="/contact" /><Contact /></>);
}
function PrivacyWithSEO() {
    return (<><SEO title="Privacy Policy — GiftToCash" description="Read GiftToCash's privacy policy to understand how we collect, use and protect your personal data." canonical="/privacy-policy" /><PrivacyPolicy /></>);
}
function TermsWithSEO() {
    return (<><SEO title="Terms & Conditions — GiftToCash" description="Read the GiftToCash terms and conditions governing use of our platform and services." canonical="/terms-condition" /><TermsConditions /></>);
}
function CookieWithSEO() {
    return (<><SEO title="Cookie Policy — GiftToCash" description="Learn how GiftToCash uses cookies to improve your experience on our platform." canonical="/cookie-policy" /><CookiePolicy /></>);
}
function AmlWithSEO() {
    return (<><SEO title="AML & KYC Policy — GiftToCash" description="GiftToCash's Anti-Money Laundering and Know Your Customer policy to keep our platform safe and compliant." canonical="/aml-kyc-policy" /><AmlKycPolicy /></>);
}
function AcceptableUseWithSEO() {
    return (<><SEO title="Acceptable Use Policy — GiftToCash" description="Read GiftToCash's acceptable use policy outlining permitted and prohibited uses of our platform." canonical="/acceptable-use" /><AcceptableUse /></>);
}
function ForgotPasswordWithSEO() {
    return (<><SEO title="Forgot Password — GiftToCash" description="Reset your GiftToCash account password." canonical="/forgot-password" noindex={true} /><ForgotPassword /></>);
}

function App() {
    return (
        <AuthProvider>
            <ThemeProvider>
                <I18nProvider>
                    <SettingsProvider>
                        <CurrencyProvider>
                        <BrowserRouter>
                            <Toaster position="top-right" />
                            <PageTracker />
                            <Safe><AutoTranslate /></Safe>
                            <Safe><MoneyTransition /></Safe>
                            <PWAInstallPrompt />
                            <BuddyToast />
                            <PushNotifications />
                            <GlobalChat />
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    <Route path="/" element={<LandingWithSEO />} />
                                    <Route path="/login" element={<LoginWithSEO />} />
                                    <Route path="/register" element={<RegisterWithSEO />} />
                                    <Route path="/auth/callback" element={<AuthCallback />} />
                                    <Route path="/forgot-password" element={<ForgotPasswordWithSEO />} />
                                    <Route path="/reset-password" element={<ResetPassword />} />
                                    <Route path="/two-factor" element={<TwoFactorChallenge />} />
                                    <Route path="/staff-setup" element={<StaffSetup />} />
                                    <Route path="/contact" element={<ContactWithSEO />} />
                                    <Route path="/marketplace" element={<MarketplacePageWithSEO />} />

                                    {/* Legal pages */}
                                    <Route path="/cookie-policy" element={<CookieWithSEO />} />
                                    <Route path="/privacy-policy" element={<PrivacyWithSEO />} />
                                    <Route path="/terms-condition" element={<TermsWithSEO />} />
                                    <Route path="/aml-kyc-policy" element={<AmlWithSEO />} />
                                    <Route path="/acceptable-use" element={<AcceptableUseWithSEO />} />
                                    <Route path="/dashboard/verify" element={<VerifyEmail />} />

                                    <Route path="/blog" element={<BlogWithSEO />} />
                                    <Route path="/reviews" element={<ReviewsWithSEO />} />
                                    <Route path="/sell" element={<SellCards />} />
                                    <Route path="/sell/:slug" element={<SellCard />} />
                                    <Route path="/gift-card-balance" element={<GiftCardBalance />} />
                                    <Route path="/blog/:slug" element={<BlogPost />} />
                                    <Route path="/buy-giftcard" element={<BuyGiftCard />} />
                                    <Route path="/buy-giftcard/cart" element={<CartPage />} />
                                    <Route path="/buy-giftcard" element={<BuyGiftCard />} />
                                    <Route path="/buy-giftcard/cart" element={<CartPage />} />

                                    <Route path="/dashboard" element={<ProtectedRoute role="user"><UserLayout /></ProtectedRoute>}>
                                        <Route index element={<UserDashboard />} />
                                        <Route path="exchanges" element={<MyExchanges />} />
                                        <Route path="wallet" element={<Wallet />} />
                                        <Route path="send" element={<SendCard />} />
                                        <Route path="transactions" element={<Transactions />} />
                                        <Route path="marketplace" element={<Marketplace />} />
                                        <Route path="support" element={<Support />} />
                                        <Route path="profile" element={<Profile />} />
                                        <Route path="settings" element={<Settings />} />
                                        <Route path="rewards" element={<Rewards />} />
                                        <Route path="alerts" element={<RateAlerts />} />
                                        <Route path="verify" element={<VerifyEmail />} />
                                        <Route path="kyc" element={<Kyc />} />
                                        <Route path="gift-card-orders" element={<MyGiftCardOrders />} />
                                    </Route>

                                    <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
                                        <Route index element={<AdminDashboard />} />
                                        <Route path="exchanges" element={<AdminExchanges />} />
                                        <Route path="users" element={<AdminUsers />} />
                                        <Route path="workers" element={<AdminWorkers />} />
                                        <Route path="kyc" element={<AdminKyc />} />
                                        <Route path="gift-card-orders" element={<MyGiftCardOrders />} />
                                        <Route path="giftcards" element={<AdminGiftCards />} />
                                        <Route path="payout-methods" element={<AdminPayoutMethods />} />
                                        <Route path="broadcasts" element={<AdminBroadcasts />} />
                                        <Route path="accounting" element={<AdminAccounting />} />
                                        <Route path="live-feed" element={<AdminLiveFeed />} />
                                        <Route path="transactions" element={<AdminTransactions />} />
                                        <Route path="withdrawals" element={<AdminWithdrawals />} />
                                        <Route path="tickets" element={<AdminTickets />} />
                                        <Route path="notifications" element={<AdminNotifications />} />
                                        <Route path="settings" element={<AdminSettings />} />
                                        <Route path="logs" element={<AdminLogs />} />
                                        <Route path="blog" element={<AdminBlog />} />
                                        <Route path="marketplace" element={<AdminMarketplace />} />
                                        <Route path="giftcard-store" element={<AdminGiftStore />} />
                                        <Route path="ads" element={<AdminAds />} />
                                        <Route path="ad-profit" element={<AdminAdProfit />} />
                                        <Route path="social" element={<AdminSocial />} />
                                        <Route path="email-campaigns" element={<AdminEmailCampaigns />} />
                                        <Route path="ai-studio" element={<AdminAIStudio />} />
                                        <Route path="rewards" element={<AdminRewards />} />
                                        <Route path="safety" element={<AdminSafety />} />
                                        <Route path="google-ads" element={<AdminGoogleAds />} />
                                        <Route path="tiktok-ads" element={<AdminTikTok />} />
                                        <Route path="video-studio" element={<AdminVideoStudio />} />
                                        <Route path="system" element={<AdminSystem />} />
                                    </Route>

                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </Suspense>
                        </BrowserRouter>
                        </CurrencyProvider>
                    </SettingsProvider>
                </I18nProvider>
            </ThemeProvider>
        </AuthProvider>
    );
}

export default App;




